import { useEffect, useState } from "react";
import { APP_NAME } from "@/lib/brand";
import { toast } from "sonner";
import { Copy, Download, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getErrorMessage } from "@/lib/errors";

const group = (s) => (s || "").replace(/(.{4})/g, "$1 ").trim();

/**
 * Setting up two-step sign-in: the key goes into an authenticator app, the app shows a 6-digit code, the code proves it works, and then the
 * backup codes are shown once. `token` is the short enrolment token from a sign-in that has no session yet (otherwise the signed-in session is used).
 * onDone receives what the server returned when it finished (it has an access_token when the person had no session yet).
 */
export function MfaEnroll({ token, onDone }) {
  const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
  const [setup, setSetup] = useState(null);
  const [methods, setMethods] = useState(null);           // the ways this person may get a code; null while we ask
  const [choice, setChoice] = useState("APP");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const start = async (method, number) => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/enroll/start", method === "APP" ? undefined : { method, phone: number || null }, { headers });
      setSetup(data); setCode("");
      if (method !== "APP") toast.success(`Code sent to ${data.sent_to}`);
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setBusy(false); }
  };

  useEffect(() => {
    let live = true;
    (async () => {
      let list = ["APP"];
      try { const r = await api.get("/auth/mfa/methods", { headers }); if (Array.isArray(r?.data?.methods) && r.data.methods.length) list = r.data.methods; } catch { /* the app is always allowed */ }
      if (!live) return;
      setMethods(list);
      if (list.length === 1) start("APP");               // only the app: straight to its key, as before
    })();
    return () => { live = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const confirm = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api.post("/auth/mfa/enroll/confirm", { code }, { headers }); setResult(data); toast.success("Two-step sign-in is on"); }
    catch (err) { toast.error(getErrorMessage(err)); } finally { setBusy(false); }
  };
  const copy = (text) => navigator.clipboard?.writeText(text).then(() => toast.success("Copied")).catch(() => {});
  const download = () => {
    const blob = new Blob([`${APP_NAME} backup codes\nEach works once.\n\n${result.backup_codes.join("\n")}\n`], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = "backup-codes.txt"; a.click();
  };

  if (result) {
    return (
      <div className="space-y-3" data-testid="mfa-backup-codes">
        <p className="text-sm font-medium text-foreground">Keep these backup codes somewhere safe.</p>
        <p className="text-xs text-muted-foreground">Each one works once, if you lose your phone. They are not shown again.</p>
        <ul className="grid grid-cols-2 gap-1 rounded-lg border border-border bg-muted/40 p-3 font-mono text-sm" data-testid="mfa-codes-list">
          {result.backup_codes.map((c) => <li key={c}>{c}</li>)}
        </ul>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => copy(result.backup_codes.join("\n"))} data-testid="mfa-copy-codes"><Copy className="h-3.5 w-3.5" /> Copy</Button>
          <Button type="button" variant="outline" size="sm" onClick={download} data-testid="mfa-download-codes"><Download className="h-3.5 w-3.5" /> Download</Button>
          <Button type="button" size="sm" className="ml-auto" onClick={() => onDone?.(result)} data-testid="mfa-saved-codes">I have saved them</Button>
        </div>
      </div>
    );
  }
  if (!setup && methods && methods.length > 1) {
    const LABEL = { APP: ["Authenticator app", "Most secure. A code from an app on your phone; works without signal."], EMAIL: ["E-mail", "We e-mail a code to your sign-in address each time."], SMS: ["Text message", "We text a code to your mobile number each time."] };
    return (
      <div className="space-y-3" data-testid="mfa-choose">
        <p className="text-sm font-medium text-foreground">How do you want to get your code?</p>
        {methods.map((m) => (
          <label key={m} className={`flex cursor-pointer items-start gap-2 rounded-lg border p-3 ${choice === m ? "border-primary" : "border-border"}`}>
            <input type="radio" name="mfa-method" checked={choice === m} onChange={() => setChoice(m)} data-testid={`mfa-choose-${m.toLowerCase()}`} className="mt-1" />
            <span><span className="block text-sm font-medium">{LABEL[m][0]}</span><span className="block text-xs text-muted-foreground">{LABEL[m][1]}</span></span>
          </label>))}
        {choice === "SMS" && (
          <div><Label htmlFor="mfa-phone">Mobile number</Label>
            <Input id="mfa-phone" type="tel" placeholder="+91 98765 43210" value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1.5 w-56" data-testid="mfa-phone" /></div>)}
        <Button type="button" disabled={busy || (choice === "SMS" && phone.replace(/\D/g, "").length < 10)} onClick={() => start(choice, phone)} data-testid="mfa-choose-continue">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Continue</Button>
      </div>
    );
  }
  if (!setup) return <p className="text-sm text-muted-foreground" data-testid="mfa-enroll-loading">Preparing…</p>;
  if (setup.method && setup.method !== "APP") {
    return (
      <form onSubmit={confirm} className="space-y-4" data-testid="mfa-enroll-sent">
        <p className="text-sm text-foreground" data-testid="mfa-sent-note">We sent a 6-digit code to <b>{setup.sent_to}</b>. It works once and expires in 10 minutes.</p>
        <div><Label htmlFor="mfa-code">Code we sent</Label>
          <Input id="mfa-code" inputMode="numeric" autoComplete="one-time-code" maxLength={8} value={code} onChange={(e) => setCode(e.target.value)} className="mt-1.5 w-40 font-mono text-lg tracking-widest" data-testid="mfa-code" /></div>
        <div className="flex items-center gap-3">
          <Button type="submit" disabled={busy || code.replace(/\D/g, "").length !== 6} data-testid="mfa-confirm">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Turn on two-step sign-in</Button>
          <button type="button" disabled={busy} className="text-xs text-primary underline" onClick={() => start(setup.method, phone)} data-testid="mfa-send-again">Send again</button>
        </div>
      </form>
    );
  }
  return (
    <form onSubmit={confirm} className="space-y-4" data-testid="mfa-enroll">
      <ol className="list-decimal space-y-2 pl-5 text-sm text-foreground">
        <li>Open an authenticator app (Google Authenticator, Microsoft Authenticator, Authy or similar).</li>
        <li>Add an account and type in this key{" "}
          <button type="button" className="text-xs text-primary underline" onClick={() => copy(setup.secret)} data-testid="mfa-copy-secret">copy</button>
          <div className="mt-1 rounded-md border border-border bg-muted/40 px-3 py-2 font-mono text-base tracking-wider" data-testid="mfa-secret">{group(setup.secret)}</div>
          <a href={setup.otpauth_uri} className="mt-1 inline-block text-xs text-primary underline" data-testid="mfa-otpauth-link">On a phone? Open it in your authenticator app</a>
        </li>
        <li>Type the 6-digit code the app shows now.</li>
      </ol>
      <div><Label htmlFor="mfa-code">Code from the app</Label>
        <Input id="mfa-code" inputMode="numeric" autoComplete="one-time-code" maxLength={8} value={code} onChange={(e) => setCode(e.target.value)} className="mt-1.5 w-40 font-mono text-lg tracking-widest" data-testid="mfa-code-input" /></div>
      <Button type="submit" disabled={busy || code.replace(/\D/g, "").length !== 6} data-testid="mfa-confirm">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Turn on two-step sign-in</Button>
    </form>
  );
}
